import { ForbiddenError } from "./forbidden-error";
import type { AppAbility } from "./types";

// ---------------------------------------------------------------------------
// Filter
//
// Turns the caller's merged, interpolated rules for one action/subject pair
// into a shape a database adapter can fold into a WHERE clause. The rule list
// stays the single source of truth for list endpoints — there is no
// hand-written filter that can drift away from it.
//
// The translation is deliberately narrower than what CASL can evaluate. It
// admits exactly what ROLE_PERMISSIONS produces today — scalar equality and
// `$in` over scalars — and throws on everything else, because a condition
// that is dropped instead of translated widens a list query silently.
// ---------------------------------------------------------------------------

export type FilterScalar = string | number | boolean | null;
export type FilterCondition = FilterScalar | { $in: Array<string | number | boolean> };
export type FilterConditions = Record<string, FilterCondition>;

export interface CitadelFilter {
	// OR-ed grants — a row must match at least one entry. Never empty: an
	// unconditioned grant returns `null` instead, and a pair with no grant at all
	// throws in `rulesToFilter`, so no adapter ever receives a filter whose
	// natural fold (`OR` of nothing) would lift the restriction.
	allow: FilterConditions[];
}

// Raised when a rule exists but cannot be expressed as a WHERE clause. This
// is a programming error surfacing loudly (500), not a refusal (403): the fix
// is always in the rule or the adapter, never in the caller's permissions.
export class FilterTranslationError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "FilterTranslationError";
	}
}

/**
 * Merge the ability's rules for one action/subject pair into a `CitadelFilter`.
 *
 * Every rule is a grant, so the merge is a union: conditioned grants accumulate
 * into `allow` and OR together, and an unconditioned grant admits every row,
 * which leaves the query unrestricted whatever else the caller carries.
 *
 * No surviving grant throws `ForbiddenError` rather than returning an empty
 * filter, so an unguarded list endpoint refuses instead of running an
 * unfiltered query.
 *
 * @param ability - The caller's built ability, conditions already interpolated.
 * @param action - The action being filtered for.
 * @param subjectName - The subject whose rows the query returns.
 * @returns The merged filter, or `null` when the caller is unrestricted.
 */
export function rulesToFilter(
	ability: AppAbility,
	action: string,
	subjectName: string,
): CitadelFilter | null {
	const rules = ability.rulesFor(action, subjectName);

	const allow: FilterConditions[] = [];

	for (const rule of rules) {
		// Conditions with no keys match every row, same as no conditions.
		const conditions =
			rule.conditions && Object.keys(rule.conditions).length > 0
				? (rule.conditions as Record<string, unknown>)
				: undefined;

		// An unconditioned grant already admits every row the query can return,
		// so no other grant can narrow it further.
		if (!conditions) return null;

		allow.push(toFilterConditions(conditions, `can ${action} ${subjectName}`));
	}

	if (allow.length === 0) throw new ForbiddenError(action, subjectName);
	return { allow };
}

function toFilterConditions(conditions: Record<string, unknown>, rule: string): FilterConditions {
	return Object.fromEntries(
		Object.entries(conditions).map(([key, value]) => {
			// A `$`-prefixed key would be a top-level operator ($or, $and) and a
			// dotted key a nested path — neither maps onto a column.
			if (key.startsWith("$") || key.includes(".")) {
				throw new FilterTranslationError(`${rule}: condition key "${key}" is not a plain column`);
			}
			return [key, toFilterCondition(key, value, rule)];
		}),
	);
}

function toFilterCondition(key: string, value: unknown, rule: string): FilterCondition {
	if (isScalar(value)) return value;

	if (value !== null && typeof value === "object" && !Array.isArray(value)) {
		const operators = Object.keys(value);
		if (operators.length === 1 && operators[0] === "$in") {
			const entries = (value as { $in: unknown }).$in;
			// A non-array here is usually an uninterpolated `${user.x}` placeholder;
			// `null` entries are excluded because SQL `IN (NULL)` never matches
			// while CASL's `$in` does.
			if (Array.isArray(entries) && entries.every((entry) => isScalar(entry) && entry !== null)) {
				return { $in: entries as Array<string | number | boolean> };
			}
			throw new FilterTranslationError(
				`${rule}: "${key}" has a $in that is not an array of scalars — a dropped condition would widen the query, so translation refuses`,
			);
		}
		throw new FilterTranslationError(
			`${rule}: "${key}" uses ${operators.join(", ")} — only scalar equality and $in translate to SQL; anything else must fail closed`,
		);
	}

	throw new FilterTranslationError(
		`${rule}: "${key}" holds a value that is neither a scalar nor a $in array`,
	);
}

function isScalar(value: unknown): value is FilterScalar {
	return (
		value === null ||
		typeof value === "string" ||
		typeof value === "number" ||
		typeof value === "boolean"
	);
}
